import { SortingRule } from 'react-table';
import { IPaymentData } from './payment-columns';

/**
 * Client-side sorting function for payment data
 * Similar to super-dial-sort pattern but optimized for payment dates (date-only, no time component)
 */
export function sortPayments(
    data: IPaymentData[],
    sortBy: SortingRule<IPaymentData>[],
): IPaymentData[] {
    if (!sortBy || sortBy.length === 0) {
        return data;
    }

    // Create a copy to avoid mutating the original array
    const sortedData = [...data];

    sortedData.sort((a, b) => {
        for (const sort of sortBy) {
            const { id, desc } = sort;
            let aValue: any;
            let bValue: any;

            // Get the payment values based on column id
            switch (id) {
                case 'paymentDate':
                    aValue = a.paymentDate;
                    bValue = b.paymentDate;
                    break;
                case 'totalAmount':
                    aValue = a.totalAmount?.amount;
                    bValue = b.totalAmount?.amount;
                    break;
                case 'unallocatedAmount':
                    aValue = a.unallocatedAmount?.amount;
                    bValue = b.unallocatedAmount?.amount;
                    break;
                case 'allocatedAmount':
                    aValue = a.allocatedAmount?.amount;
                    bValue = b.allocatedAmount?.amount;
                    break;
                case 'refundAmount':
                    aValue = a.refundAmount?.amount;
                    bValue = b.refundAmount?.amount;
                    break;
                default:
                    return 0; // Unknown column, no sort
            }

            // Handle null/undefined/empty values (similar to super-dial-sort)
            if (aValue === null || aValue === undefined || aValue === '') {
                return desc ? -1 : 1;
            }
            if (bValue === null || bValue === undefined || bValue === '') {
                return desc ? 1 : -1;
            }

            // Compare values based on type
            let comparison = 0;

            // Date comparison (payment dates are date-only strings like "2024-10-03")
            if (id === 'paymentDate') {
                const aDate = new Date(aValue);
                const bDate = new Date(bValue);
                if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
                    comparison = aDate.getTime() - bDate.getTime();
                } else {
                    comparison = String(aValue).localeCompare(String(bValue));
                }
            }
            // Number comparison (for amounts)
            else if (id === 'totalAmount' || id === 'unallocatedAmount' || id === 'allocatedAmount' || id === 'refundAmount') {
                const aNum = Number(aValue);
                const bNum = Number(bValue);
                if (!isNaN(aNum) && !isNaN(bNum)) {
                    comparison = aNum - bNum;
                } else {
                    comparison = String(aValue).localeCompare(String(bValue));
                }
            }
            // String comparison
            else {
                comparison = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
            }

            // Apply sort direction
            if (comparison !== 0) {
                return desc ? -comparison : comparison;
            }
        }

        return 0;
    });

    return sortedData;
}

